import * as Sentry from "@sentry/react";

/**
 * measureAction — wrap a user action (button click, form submit) in a Sentry
 * performance span so it appears on the Performance dashboard.
 *
 *   await measureAction("checkout.submit", async () => {
 *     const res = await measureFetch("checkout.api", () => fetch("/api/checkout"));
 *     return res.json();
 *   });
 *
 * The span always finishes (success or failure); errors are captured to Sentry
 * with the same op so latency and error rate stay correlated.
 */
export function measureAction<T>(
  name: string,
  fn: () => Promise<T> | T,
  attributes: Record<string, string | number | boolean> = {},
): Promise<T> {
  return Sentry.startSpan(
    { name, op: "ui.action", attributes: { source: "measureAction", ...attributes } },
    async (span) => {
      const started = performance.now();
      try {
        const result = await fn();
        span?.setStatus({ code: 1 }); // OK
        return result;
      } catch (err) {
        span?.setStatus({ code: 2, message: (err as Error)?.message });
        Sentry.captureException(err, { tags: { action: name } });
        throw err;
      } finally {
        span?.setAttribute("duration_ms", Math.round(performance.now() - started));
      }
    },
  );
}

/**
 * measureFetch — wrap a backend/API call in a child span so the network leg
 * is visible inside a parent measureAction() span.
 */
export function measureFetch<T>(name: string, fn: () => Promise<T>): Promise<T> {
  return Sentry.startSpan(
    { name, op: "http.client", attributes: { source: "measureFetch" } },
    async (span) => {
      const started = performance.now();
      try {
        const result = await fn();
        span?.setStatus({ code: 1 });
        return result;
      } catch (err) {
        span?.setStatus({ code: 2, message: (err as Error)?.message });
        throw err;
      } finally {
        span?.setAttribute("duration_ms", Math.round(performance.now() - started));
      }
    },
  );
}